import { defineStore } from 'pinia';

import { updateSuperProperties } from '@/helpers/analytics/mixpanel';
import { axios } from '@/helpers/axios';

import { useAuthStore } from './auth';

type IntercomCommand = ((...args: unknown[]) => void) & { q?: unknown[][] };
let widgetBooted = false;

export const useIntercomStore = defineStore('intercom', {
  state: () => ({}),
  actions: {
    async fetchIntercom() {
      try {
        if (!useAuthStore().isUserLogged) return;
        const response = await axios.get('/intercom');
        if (!response.data.success) return;
        const data = response.data.data;
        const shopName = (typeof data.shop === 'object' && data.shop !== null) ? data.shop.shop_name : data.shop;
        // Preserve the original attributes while omitting personal name and email fields.
        const settings: Record<string, unknown> = {
          api_base: 'https://api-iam.intercom.io',
          app_id: 'w7z8c9mt',
          memberid: data.member_id,
          user_id: String(data.member_id),
          plan_id: data.plan_id,
          shop: shopName,
          shops_count: data.shops_count,
          plan: data.plan,
          created_at: data.created_at,
          user_hash: data.user_hash,
          kl: null,
          kt: null,
          lh_quota: data.lh_quota,
          cs_quota: data.cs_quota,
          kt_quota: data.kt_quota,
          kl_quota: data.kl_quota,
          active_listings: data.active_listings,
          sales: data.sales,
          ref: data.referrer,
          'Subscribed to Newsletter': data.newsletter,
          'Last Login': data.last_login,
          'Viewed Plans Page': data.plan_page_visit,
          hc_missing_image_count: data.hc_missing_image_count,
          hc_one_word_tag_count: data.hc_one_word_tag_count,
          hc_missing_tag_count: data.hc_missing_tag_count,
          hc_spelling_issue_count: data.hc_spelling_issue_count,
          zero_revenue_customer: data['Zero-revenue customer'],
          onboarding_goal: data.onboarding_goal,
          searched_for_keyword: data.searched_for_keyword,
          added_keyword_to_list: data.added_keyword_to_list,
          trialed: data.trialed,
          beta: data.beta,
          is_haa: data.is_haa,
          haa_status: data.haa_status,
          haa_expiry_date: data.haa_expiry_date,
          'Plan Period': data['Plan Period'],
        };
        if (Array.isArray(data.lowest_views_listing)) {
          data.lowest_views_listing.forEach((value, index) => {
            settings[`lowest_views_${index + 1}`] = value;
          });
        }

        updateSuperProperties({ 'Shop Listings': data.active_listings, 'Shop Sales': data.sales });

        const w = window as unknown as {
          intercomSettings: Record<string, unknown>;
          Intercom?: IntercomCommand;
          clarity?: (...args: unknown[]) => void;
        };
        w.intercomSettings = settings;
        if (typeof w.clarity === 'function') {
          w.clarity('set', 'member_id', data.member_id.toString());
          w.clarity('set', 'plan_id', data.plan_id.toString());
          w.clarity('set', 'shop_name', shopName);
          w.clarity('set', 'plan_type', data.plan);
        }

        if (typeof w.Intercom !== 'function') {
          const queued: unknown[][] = [];
          const stub: IntercomCommand = (...args: unknown[]) => { queued.push(args); };
          stub.q = queued;
          w.Intercom = stub;
          const loadWidget = () => {
            const script = document.createElement('script');
            script.async = true;
            script.src = 'https://widget.intercom.io/widget/w7z8c9mt';
            document.head.appendChild(script);
          };
          if (document.readyState === 'complete') loadWidget();
          else window.addEventListener('load', loadWidget, { once: true });
        }

        if (!widgetBooted) {
          // Clear the previous session, then boot using the member's verified ID.
          w.Intercom('shutdown');
          w.Intercom('boot', settings);
          widgetBooted = true;
        } else {
          w.Intercom('reattach_activator');
          w.Intercom('update', settings);
        }
      } catch {
        // Axios errors include request/response data; never log the error object.
        console.error('Unable to load support widget');
      }
    },
  },
});
